import { BatteryChemistry, type ChemistryPreset, type TemperatureDeratingPoint } from '@brt/shared-types'

const LIFEPO4_CURVE: TemperatureDeratingPoint[] = [
  { tempC: -20, factor: 0.70 },
  { tempC: -10, factor: 0.82 },
  { tempC: 0, factor: 0.90 },
  { tempC: 10, factor: 0.96 },
  { tempC: 20, factor: 1.00 },
  { tempC: 25, factor: 1.00 },
  { tempC: 30, factor: 1.00 },
  { tempC: 40, factor: 0.98 },
  { tempC: 50, factor: 0.92 },
  { tempC: 60, factor: 0.85 },
]

const LIION_CURVE: TemperatureDeratingPoint[] = [
  { tempC: -20, factor: 0.65 },
  { tempC: -10, factor: 0.78 },
  { tempC: 0, factor: 0.88 },
  { tempC: 10, factor: 0.95 },
  { tempC: 20, factor: 1.00 },
  { tempC: 25, factor: 1.00 },
  { tempC: 30, factor: 1.00 },
  { tempC: 40, factor: 0.97 },
  { tempC: 50, factor: 0.90 },
  { tempC: 60, factor: 0.82 },
]

const LEAD_ACID_CURVE: TemperatureDeratingPoint[] = [
  { tempC: -20, factor: 0.60 },
  { tempC: -10, factor: 0.72 },
  { tempC: 0, factor: 0.80 },
  { tempC: 10, factor: 0.88 },
  { tempC: 20, factor: 0.95 },
  { tempC: 25, factor: 1.00 },
  { tempC: 30, factor: 1.00 },
  { tempC: 40, factor: 0.95 },
  { tempC: 50, factor: 0.88 },
  { tempC: 60, factor: 0.80 },
]

const NIMH_NICD_CURVE: TemperatureDeratingPoint[] = [
  { tempC: -20, factor: 0.75 },
  { tempC: -10, factor: 0.85 },
  { tempC: 0, factor: 0.92 },
  { tempC: 10, factor: 0.97 },
  { tempC: 20, factor: 1.00 },
  { tempC: 25, factor: 1.00 },
  { tempC: 40, factor: 0.96 },
  { tempC: 60, factor: 0.85 },
]

const ALKALINE_CURVE: TemperatureDeratingPoint[] = LIION_CURVE

export const CHEMISTRY_PRESETS: ReadonlyMap<BatteryChemistry, ChemistryPreset> = new Map([
  [BatteryChemistry.LI_FEPO4, {
    chemistry: BatteryChemistry.LI_FEPO4,
    nominalCellVoltage: 3.2,
    maxDoD: 0.90,
    defaultDoD: 0.80,
    peukertExponent: 1.05,
    maxSafeCRate: 1.0,
    peakCRate: 3.0,
    cutoffVoltage: 2.5,
    minTempC: -20,
    maxTempC: 60,
    selfDischargePerDay: 0.0003,
    cycleLifeRange: '3000–6000',
    ratedDischargeHours: 1,
    deratingCurve: LIFEPO4_CURVE,
  }],
  [BatteryChemistry.LI_ION, {
    chemistry: BatteryChemistry.LI_ION,
    nominalCellVoltage: 3.7,
    maxDoD: 0.80,
    defaultDoD: 0.70,
    peukertExponent: 1.05,
    maxSafeCRate: 1.0,
    peakCRate: 2.0,
    cutoffVoltage: 2.8,
    minTempC: -20,
    maxTempC: 60,
    selfDischargePerDay: 0.0005,
    cycleLifeRange: '500–1000',
    ratedDischargeHours: 1,
    deratingCurve: LIION_CURVE,
  }],
  [BatteryChemistry.LEAD_ACID_FLOODED, {
    chemistry: BatteryChemistry.LEAD_ACID_FLOODED,
    nominalCellVoltage: 2.0,
    maxDoD: 0.50,
    defaultDoD: 0.50,
    peukertExponent: 1.25,
    maxSafeCRate: 0.2,
    peakCRate: 0.5,
    cutoffVoltage: 1.75,
    minTempC: -20,
    maxTempC: 50,
    selfDischargePerDay: 0.001,
    cycleLifeRange: '300–500',
    ratedDischargeHours: 20,
    deratingCurve: LEAD_ACID_CURVE,
  }],
  [BatteryChemistry.AGM, {
    chemistry: BatteryChemistry.AGM,
    nominalCellVoltage: 2.0,
    maxDoD: 0.50,
    defaultDoD: 0.50,
    peukertExponent: 1.15,
    maxSafeCRate: 0.3,
    peakCRate: 0.8,
    cutoffVoltage: 1.75,
    minTempC: -20,
    maxTempC: 50,
    selfDischargePerDay: 0.0005,
    cycleLifeRange: '400–700',
    ratedDischargeHours: 20,
    deratingCurve: LEAD_ACID_CURVE,
  }],
  [BatteryChemistry.GEL, {
    chemistry: BatteryChemistry.GEL,
    nominalCellVoltage: 2.0,
    maxDoD: 0.50,
    defaultDoD: 0.50,
    peukertExponent: 1.12,
    maxSafeCRate: 0.2,
    peakCRate: 0.5,
    cutoffVoltage: 1.75,
    minTempC: -20,
    maxTempC: 50,
    selfDischargePerDay: 0.0003,
    cycleLifeRange: '500–1000',
    ratedDischargeHours: 20,
    deratingCurve: LEAD_ACID_CURVE,
  }],
  [BatteryChemistry.NI_MH, {
    chemistry: BatteryChemistry.NI_MH,
    nominalCellVoltage: 1.2,
    maxDoD: 1.00,
    defaultDoD: 0.90,
    peukertExponent: 1.05,
    maxSafeCRate: 2.0,
    peakCRate: 5.0,
    cutoffVoltage: 1.0,
    minTempC: -20,
    maxTempC: 50,
    selfDischargePerDay: 0.01,
    cycleLifeRange: '300–500',
    ratedDischargeHours: 10,
    deratingCurve: NIMH_NICD_CURVE,
  }],
  [BatteryChemistry.NI_CD, {
    chemistry: BatteryChemistry.NI_CD,
    nominalCellVoltage: 1.2,
    maxDoD: 1.00,
    defaultDoD: 0.90,
    peukertExponent: 1.03,
    maxSafeCRate: 5.0,
    peakCRate: 10.0,
    cutoffVoltage: 1.0,
    minTempC: -40,
    maxTempC: 60,
    selfDischargePerDay: 0.005,
    cycleLifeRange: '500–1000',
    ratedDischargeHours: 10,
    deratingCurve: NIMH_NICD_CURVE,
  }],
  [BatteryChemistry.ALKALINE, {
    chemistry: BatteryChemistry.ALKALINE,
    nominalCellVoltage: 1.5,
    maxDoD: 1.00,
    defaultDoD: 0.95,
    peukertExponent: 1.00,
    maxSafeCRate: 0.05,
    peakCRate: 0.1,
    cutoffVoltage: 0.9,
    minTempC: -20,
    maxTempC: 55,
    selfDischargePerDay: 0.0001,
    cycleLifeRange: '1',
    ratedDischargeHours: 1,
    deratingCurve: ALKALINE_CURVE,
  }],
])

export function getChemistryPreset(chemistry: BatteryChemistry): ChemistryPreset {
  const preset = CHEMISTRY_PRESETS.get(chemistry)
  if (!preset) {
    return {
      chemistry: BatteryChemistry.CUSTOM,
      nominalCellVoltage: 3.7,
      maxDoD: 0.80,
      defaultDoD: 0.80,
      peukertExponent: 1.0,
      maxSafeCRate: 1.0,
      peakCRate: 2.0,
      cutoffVoltage: 2.5,
      minTempC: -20,
      maxTempC: 60,
      selfDischargePerDay: 0.001,
      cycleLifeRange: 'Unknown',
      ratedDischargeHours: 1,
      deratingCurve: LIION_CURVE,
    }
  }
  return preset
}
